import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { Link } from "react-router-dom";
import SEO from "../SEO";
import { FaShieldAlt, FaLock, FaUserSecret, FaHistory } from "react-icons/fa";

const PrivacyPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Information We Collect",
      icon: <FaUserSecret />,
      list: [
        "Personal Identification: Full Name, Email, Phone Number, Date of Birth, PAN, Aadhaar, and Identity Documents.",
        "Financial Context: Bank Statements, Salary Slips, and Credit History for accurate loan assessment.",
        "Digital Interaction: IP addresses, browser types, and usage data to improve our security protocols.",
      ],
    },
    {
      title: "2. How We Use Your Data",
      icon: <FaShieldAlt />,
      list: [
        "Loan Processing: Verifying eligibility and managing your repayment schedules.",
        "Compliance & Safety: Adhering to KYC, AML, and other institutional regulations.",
        "Experience Enhancement: Personalizing our services to better serve your financial goals.",
      ],
    },
    {
      title: "3. Data Sharing Integrity",
      icon: <FaLock />,
      list: [
        "Verified Partners: Working with Credit Bureaus and Payment Gateways to ensure seamless transactions.",
        "Regulatory Compliance: Reporting to legal authorities as mandated by Indian financial laws.",
        `Institutional Sync: Secure data sharing with ${import.meta.env.VITE_COMPANY_NBFC} for official processing.`,
      ],
    },
    {
      title: "Your Privacy Rights",
      content: (
        <div className="space-y-4">
          <p>
            Transparency is at our core. You have the full right to access, rectify, or request the deletion of your personal data. We ensure your digital footprint remains under your control.
          </p>
          <div className="p-6 bg-secondary/5 border border-secondary/10 rounded-2xl">
            <p className="text-sm font-medium">
              Need to remove your data? Visit our{" "}
              <Link to="/account-delete-policy" className="text-secondary font-black hover:underline">
                User Data Deletion Policy
              </Link>
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Privacy Policy"
        description={`Your privacy is our priority. Learn how ${import.meta.env.VITE_COMPANY_NAME} protects your personal and financial information.`}
        keywords={`privacy policy, data protection, secure lending, fintech privacy`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20">
             <div className="flex items-center gap-3 px-5 py-2 bg-white border border-secondary/20 shadow-sm w-fit rounded-full mb-8">
                <FaHistory className="text-secondary text-[10px]" />
                <span className="text-[10px] font-black tracking-widest text-text-secondary uppercase">Last Revision: April 2024</span>
              </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter mb-6 text-text-primary">
              Privacy <span className="text-secondary">Matters.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-xl font-medium leading-relaxed max-w-2xl">
              Understand how we protect your information at {import.meta.env.VITE_COMPANY_NAME}. We are committed to maintaining the highest standards of data security.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-2xl shrink-0">
                    {section.icon || <FaLock />}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <div className="text-text-secondary text-lg leading-relaxed font-medium">
                        {section.content}
                      </div>
                    )}
                    {section.list && (
                      <ul className="space-y-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-base">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Generic sections simplified */}
            <div className="bg-primary text-white p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl font-black mb-6">Our Security Pledge</h3>
                <p className="text-white/80 text-lg leading-relaxed font-medium mb-10">
                  We employ enterprise-grade encryption and secure infrastructure to safeguard your digital footprint. At {import.meta.env.VITE_COMPANY_NAME}, your trust is our most valuable asset.
                </p>
                <div className="grid md:grid-cols-2 gap-8 text-sm">
                  <div className="space-y-4">
                    <div className="font-black uppercase tracking-widest text-secondary">Contact Support</div>
                    <p className="font-medium opacity-80">{import.meta.env.VITE_COMPANY_EMAIL}</p>
                  </div>
                  <div className="space-y-4">
                    <div className="font-black uppercase tracking-widest text-secondary">Global Compliance</div>
                    <p className="font-medium opacity-80">RBI-Compliant Standards</p>
                  </div>
                </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
